import { useState, useEffect } from 'react';
import AdminLayout from '../components/admin/AdminLayout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { Plus, Trash2, X, Megaphone, Send } from 'lucide-react';
import axios from 'axios';
import {
  PageHeader,
  ConfirmDialog,
  EmptyState,
  LoadingState,
  StatusBadge,
} from '../components/admin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminAnnouncements() {
  const { lang, t } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ title: '', title_tr: '', content: '', content_tr: '', target: 'all', priority: 'normal' });

  // Confirm dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/announcements`, { headers: authHeaders() });
      setItems(r.data.announcements);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchItems(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!(form.title || form.title_tr) || !form.content) {
      toast.warning(t('admin.ann_validation'));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/announcements`, form, { headers: authHeaders() });
      toast.success(t('admin.ann_published'));
      setShowForm(false);
      setForm({ title: '', title_tr: '', content: '', content_tr: '', target: 'all', priority: 'normal' });
      fetchItems();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setSubmitting(false);
    }
  };

  const toggle = async (id, active) => {
    try {
      await axios.put(`${API}/admin/announcements/${id}`, { is_active: !active }, { headers: authHeaders() });
      toast.success(active ? t('admin.ann_deactivated') : t('admin.ann_activated'));
      fetchItems();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  const requestDelete = (id) => {
    setConfirmId(id);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    setConfirmLoading(true);
    try {
      await axios.delete(`${API}/admin/announcements/${confirmId}`, { headers: authHeaders() });
      toast.success(t('admin.ann_deleted'));
      fetchItems();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmId(null);
    }
  };

  const priorityVariant = (p) => p === 'high' ? 'danger' : p === 'normal' ? 'info' : 'default';
  const priorityLabel = (p) => t(`admin.ann_label_${p}`) || p;
  const targetLabel = (tgt) => t(`admin.ann_label_${tgt}`) || tgt;

  return (
    <AdminLayout title="Announcements" titleTr={t('admin.ann_page_title')}>
      <PageHeader
        title={t('admin.ann_page_title')}
        subtitle={t('admin.ann_page_subtitle')}
        breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: t('admin.ann_page_title') }]}
        actions={
          <button
            onClick={() => setShowForm(true)}
            className="btn-primary px-4 py-2 text-sm flex items-center gap-2"
            data-testid="add-announcement-btn"
          >
            <Plus size={16} /> {t('admin.ann_new')}
          </button>
        }
      />

      {showForm && (
        <div className="card-clay p-5 mb-6 space-y-3">
          <div className="flex justify-between items-center">
            <h3 className="font-bold">{t('admin.ann_new')}</h3>
            <button onClick={() => setShowForm(false)}><X size={18} /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <input
              placeholder={t('admin.ann_title_en')}
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
              className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 focus:border-[var(--brand-primary)] focus:outline-none font-semibold text-sm"
              data-testid="ann-title-input"
            />
            <input
              placeholder={t('admin.ann_title_tr')}
              value={form.title_tr}
              onChange={e => setForm({ ...form, title_tr: e.target.value })}
              className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 focus:border-[var(--brand-primary)] focus:outline-none font-semibold text-sm"
            />
          </div>
          <textarea
            placeholder={t('admin.ann_content')}
            value={form.content}
            onChange={e => setForm({ ...form, content: e.target.value })}
            rows={3}
            className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 focus:border-[var(--brand-primary)] focus:outline-none font-semibold text-sm"
            data-testid="ann-content-input"
          />
          <div className="flex gap-3 flex-wrap">
            <select
              value={form.target}
              onChange={e => setForm({ ...form, target: e.target.value })}
              className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm"
            >
              <option value="all">{t('admin.ann_target_all')}</option>
              <option value="students">{t('admin.ann_target_students')}</option>
              <option value="instructors">{t('admin.ann_target_instructors')}</option>
            </select>
            <select
              value={form.priority}
              onChange={e => setForm({ ...form, priority: e.target.value })}
              className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm"
            >
              <option value="low">{t('admin.ann_priority_low')}</option>
              <option value="normal">{t('admin.ann_priority_normal')}</option>
              <option value="high">{t('admin.ann_priority_high')}</option>
            </select>
            <button
              onClick={save}
              disabled={submitting}
              className="btn-success px-6 py-2 text-sm flex items-center gap-2 disabled:opacity-50"
              data-testid="save-ann-btn"
            >
              <Send size={14} />
              {submitting ? t('admin.ann_publishing') : t('admin.ann_publish')}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <LoadingState variant="table" rows={4} cols={3} />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Megaphone size={40} strokeWidth={1.5} />}
          title={t('admin.ann_empty_title')}
          description={t('admin.ann_empty_desc')}
          action={{ label: t('admin.ann_new'), onClick: () => setShowForm(true) }}
        />
      ) : (
        <div className="space-y-3">
          {items.map(a => (
            <div
              key={a.announcement_id}
              className={`card-clay p-4 flex items-start gap-4 ${!a.is_active ? 'opacity-50' : ''}`}
            >
              <Megaphone
                size={20}
                className={`mt-1 flex-shrink-0 ${
                  a.priority === 'high' ? 'text-red-500' :
                  a.priority === 'normal' ? 'text-[var(--brand-primary)]' :
                  'text-[var(--text-muted)]'
                }`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h4 className="font-bold text-sm">{a.title || a.title_tr}</h4>
                  <StatusBadge variant={priorityVariant(a.priority)} label={priorityLabel(a.priority)} />
                  <StatusBadge variant="default" label={targetLabel(a.target)} />
                </div>
                <p className="text-xs text-[var(--text-muted)] line-clamp-2">{a.content}</p>
                <p className="text-[10px] text-[var(--text-muted)] mt-1">
                  {new Date(a.created_at).toLocaleDateString('tr-TR')}
                </p>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button
                  onClick={() => toggle(a.announcement_id, a.is_active)}
                  className={`px-2 py-1 rounded text-[10px] font-bold ${
                    a.is_active ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {a.is_active ? t('admin.ann_active') : t('admin.ann_inactive')}
                </button>
                <button
                  onClick={() => requestDelete(a.announcement_id)}
                  className="p-1 rounded bg-red-50 text-red-500"
                  aria-label={t('admin.btn_delete')}
                  data-testid={`del-ann-${a.announcement_id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(o) => { if (!o) { setConfirmOpen(false); setConfirmId(null); } }}
        title={t('admin.ann_delete_title')}
        description={t('admin.ann_delete_desc')}
        variant="danger"
        confirmLabel={t('admin.btn_delete')}
        onConfirm={handleConfirmDelete}
        loading={confirmLoading}
      />
    </AdminLayout>
  );
}
